// Charting preferences: the provider's default session workspace, set in Calendar settings › Visits & Charting
// and changed per session from the Layout menu. Stored per provider by the backend (charting_preference JSON).

export type TChartingLayout = 'CHART' | 'VIDEO' | 'SIDE_BY_SIDE' | 'FLOATING';

// Visit note is required, so it is always shown and has no switch (the backend's VISIT_NOTE key is ignored)
export type TChartingSection = 'PRESCRIPTIONS' | 'PRIVATE_NOTES' | 'DIAGNOSIS' | 'BILLING';

export type TChartingPreference = {
    layout: TChartingLayout;
    // true = the section is shown
    sections: Record<TChartingSection, boolean>;
};

export type TChartingPreferencePatch = {
    layout?: TChartingLayout;
    sections?: Partial<Record<TChartingSection, boolean>>;
};

export type TChartingPreferenceResult = {
    current: TChartingPreference;
    // the provider's defaults (from specialty and pay type), used by Restore defaults
    defaults: TChartingPreference;
};

export const CHARTING_LAYOUTS: { value: TChartingLayout; label: string }[] = [
    { value: 'CHART', label: 'Chart focus' },
    { value: 'VIDEO', label: 'Video focus' },
    { value: 'SIDE_BY_SIDE', label: 'Side by side' },
    { value: 'FLOATING', label: 'Floating video' },
];

// Same order as the session page
export const CHARTING_SECTIONS: { value: TChartingSection; label: string; desc?: string }[] = [
    { value: 'PRESCRIPTIONS', label: 'Prescriptions' },
    { value: 'PRIVATE_NOTES', label: 'Private notes' },
    { value: 'DIAGNOSIS', label: 'Diagnosis' },
    { value: 'BILLING', label: 'Billing codes', desc: 'Includes Notes for the Klarity billing team.' },
];

// Used before the saved preferences load; matches the backend default for a provider who takes insurance
export const DEFAULT_CHARTING_PREFERENCE: TChartingPreference = {
    layout: 'VIDEO',
    sections: {
        PRESCRIPTIONS: true,
        PRIVATE_NOTES: true,
        DIAGNOSIS: true,
        BILLING: true,
    },
};

export const isSameChartingPreference = (a?: TChartingPreference, b?: TChartingPreference) =>
    !!a &&
    !!b &&
    a.layout === b.layout &&
    CHARTING_SECTIONS.every(({ value }) => a.sections[value] === b.sections[value]);
